const API_URL = process.env.REACT_APP_BACKEND_URL;
const CSRF_STORAGE_KEY = 'brewing_csrf_token';

if (!API_URL) {
  console.error(
    'REACT_APP_BACKEND_URL is not set. Add it to frontend/.env (e.g. REACT_APP_BACKEND_URL=http://localhost:8001)'
  );
}

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
}

function getStoredCsrfToken() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(CSRF_STORAGE_KEY);
  } catch {
    return null;
  }
}

function setStoredCsrfToken(token) {
  if (typeof window === 'undefined' || !token) return;
  try {
    window.localStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch {
    // Ignore storage failures; cookie fallback still works for same-site deployments.
  }
}

function clearStoredCsrfToken() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(CSRF_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

async function request(path, options = {}) {
  const { body, timeout = 60000, responseType, ...rest } = options;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  const method = rest.method || 'GET';
  const headers = { ...rest.headers };

  const config = {
    credentials: 'include',
    signal: controller.signal,
    ...rest,
    headers,
  };

  if (body !== undefined) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      config.body = body; // let the browser set multipart Content-Type + boundary
    } else {
      config.headers = {
        'Content-Type': 'application/json',
        ...config.headers,
      };
      config.body = JSON.stringify(body);
    }
  }

  if (!['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())) {
    const csrfToken = getCookie('csrf_token') || getStoredCsrfToken();
    if (csrfToken) {
      config.headers = {
        ...config.headers,
        'X-CSRF-Token': csrfToken,
      };
    }
  }

  try {
    const response = await fetch(`${API_URL}${path}`, config);

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new ApiError(
        data.detail || `Request failed (${response.status})`,
        response.status,
        data
      );
    }

    // 204 No Content
    if (response.status === 204) return null;

    if (responseType === 'blob') return await response.blob();

    const data = await response.json();
    if (data?.csrf_token) {
      setStoredCsrfToken(data.csrf_token);
    }
    if (path === '/api/auth/logout') {
      clearStoredCsrfToken();
    }
    return data;
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new ApiError('Request timed out', 0);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

const api = {
  get: (path, options) => request(path, { method: 'GET', ...options }),
  getBlob: (path, options) => request(path, { method: 'GET', responseType: 'blob', ...options }),
  post: (path, body, options) => request(path, { method: 'POST', body, ...options }),
  put: (path, body, options) => request(path, { method: 'PUT', body, ...options }),
  delete: (path, options) => request(path, { method: 'DELETE', ...options }),
};

function trackWhatsAppClick(source, courseSlug) {
  if (!API_URL) return;
  try {
    const body = JSON.stringify({
      source,
      course_slug: courseSlug || null,
      path: typeof window !== 'undefined' ? window.location.pathname : null,
    });
    fetch(`${API_URL}/api/track/whatsapp-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch {
    // Never block the WhatsApp open if tracking fails.
  }
}

export { api, ApiError, trackWhatsAppClick };
